import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Pencil, Search, X, ChevronLeft, ChevronRight, MoreVertical } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { productMasterApi, taxMasterApi, feesMasterApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { ProductMasterListItem, CreateProductMasterRequest, UpdateProductMasterRequest, ProductType, UnitType, DiscountType, TaxMasterListItem, FeesMasterListItem } from '@/types'
import { usePermissions } from '@/contexts/PermissionContext'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]
const ALL_STATUSES = '__all__'
const ALL_TYPES = '__all__'
const NO_SELECTION = '__none__'

interface ProductFilters {
  searchTerm: string
  status: string
  type: string
}

export default function ProductMasterPage() {
  const { t } = useTranslation()
  const { canAddEdit } = usePermissions()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingProduct, setEditingProduct] = useState<ProductMasterListItem | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [filters, setFilters] = useState<ProductFilters>({
    searchTerm: '',
    status: ALL_STATUSES,
    type: ALL_TYPES,
  })
  const [formData, setFormData] = useState<CreateProductMasterRequest>({
    product_type: 'PRODUCT',
    name: '',
    unit_type: 'QUANTITY',
    unit: '',
    price: 0,
    tax_id: undefined,
    discount_type: undefined,
    discount_value: 0,
    fees_id: undefined,
    description: '',
    currency: 'INR',
  })

  const queryClient = useQueryClient()

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['products'],
    queryFn: () => productMasterApi.getAll(),
  })

  const { data: taxes = [] } = useQuery<TaxMasterListItem[]>({
    queryKey: ['taxes', 'active'],
    queryFn: () => taxMasterApi.getAll(true),
  })

  const { data: fees = [] } = useQuery<FeesMasterListItem[]>({
    queryKey: ['fees', 'active'],
    queryFn: () => feesMasterApi.getAll(true),
  })

  // Filter products based on filters
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase()
        const matchesName = product.name.toLowerCase().includes(searchLower)
        if (!matchesName) return false
      }
      if (filters.status !== ALL_STATUSES) {
        const isActive = filters.status === 'active'
        if (product.is_active !== isActive) return false
      }
      if (filters.type !== ALL_TYPES) {
        if (product.product_type !== filters.type) return false
      }
      return true
    })
  }, [products, filters])

  // Pagination calculations
  const totalRecords = filteredProducts.length
  const totalPages = Math.ceil(totalRecords / pageSize)
  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    return filteredProducts.slice(startIndex, startIndex + pageSize)
  }, [filteredProducts, currentPage, pageSize])

  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const endRecord = Math.min(currentPage * pageSize, totalRecords)

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize))
    setCurrentPage(1)
  }

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1)
  }

  const goToPreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1)
  }

  const clearFilters = () => {
    setFilters({ searchTerm: '', status: ALL_STATUSES, type: ALL_TYPES })
    setCurrentPage(1)
  }

  const hasActiveFilters = filters.searchTerm || filters.status !== ALL_STATUSES || filters.type !== ALL_TYPES

  const createMutation = useMutation({
    mutationFn: productMasterApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      setIsDialogOpen(false)
      resetForm()
      toast.success(t('masters.product.productCreated'))
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('masters.product.failedToCreate')
      toast.error(errorMessage)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProductMasterRequest }) =>
      productMasterApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      setIsDialogOpen(false)
      setEditingProduct(null)
      resetForm()
      toast.success(t('masters.product.productUpdated'))
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('masters.product.failedToUpdate')
      toast.error(errorMessage)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: productMasterApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      toast.success(t('masters.product.productDeleted'))
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('masters.product.failedToDelete')
      toast.error(errorMessage)
    },
  })

  const resetForm = () => {
    setFormData({
      product_type: 'PRODUCT',
      name: '',
      unit_type: 'QUANTITY',
      unit: '',
      price: 0,
      tax_id: undefined,
      discount_type: undefined,
      discount_value: 0,
      fees_id: undefined,
      description: '',
      currency: 'INR',
    })
    setEditingProduct(null)
  }

  const handleEdit = (product: ProductMasterListItem) => {
    setEditingProduct(product)
    setFormData({
      product_type: product.product_type,
      name: product.name,
      unit_type: product.unit_type,
      unit: product.unit || '',
      price: product.price,
      tax_id: product.tax_id,
      discount_type: product.discount_type,
      discount_value: product.discount_value,
      fees_id: product.fees_id,
      description: '',
      currency: product.currency,
    })
    setIsDialogOpen(true)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (editingProduct) {
      updateMutation.mutate({
        id: editingProduct.product_id,
        data: formData,
      })
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleOpenChange = (open: boolean) => {
    setIsDialogOpen(open)
    if (!open) {
      resetForm()
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t('masters.product.title')}</h1>
          <p className="text-muted-foreground">
            {t('masters.product.subtitle')}
          </p>
        </div>

        {canAddEdit('PRODUCT_MASTER') && (
          <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                {t('masters.product.addProduct')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSubmit}>
              <DialogHeader>
                <DialogTitle>{editingProduct ? t('masters.product.editProduct') : t('masters.product.addProduct')}</DialogTitle>
                <DialogDescription>
                  {editingProduct ? t('masters.product.updateProductDetails') : t('masters.product.createNewProduct')}
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="product_type">{t('masters.product.productType')} *</Label>
                    <Select
                      value={formData.product_type}
                      onValueChange={(value: ProductType) => setFormData({ ...formData, product_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('masters.product.selectProductType')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PRODUCT">{t('masters.product.product')}</SelectItem>
                        <SelectItem value="SERVICE">{t('masters.product.service')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="name">{t('masters.product.productName')} *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Web Development, Logo Design"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="unit_type">{t('masters.product.unitType')} *</Label>
                    <Select
                      value={formData.unit_type}
                      onValueChange={(value: UnitType) => setFormData({ ...formData, unit_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('masters.product.selectUnitType')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="HOURS">{t('masters.product.hours')}</SelectItem>
                        <SelectItem value="QUANTITY">{t('masters.product.quantity')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="unit">{t('masters.product.unit')}</Label>
                    <Input
                      id="unit"
                      value={formData.unit || ''}
                      onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                      placeholder="e.g., hrs, pcs, units"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="price">{t('masters.product.price')} *</Label>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.price || ''}
                      onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="currency">{t('masters.product.currency')} *</Label>
                    <Select
                      value={formData.currency}
                      onValueChange={(value) => setFormData({ ...formData, currency: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('masters.product.selectCurrency')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="INR">INR - Indian Rupee</SelectItem>
                        <SelectItem value="USD">USD - US Dollar</SelectItem>
                        <SelectItem value="EUR">EUR - Euro</SelectItem>
                        <SelectItem value="GBP">GBP - British Pound</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="tax_id">{t('masters.product.tax')}</Label>
                    <Select
                      value={formData.tax_id || NO_SELECTION}
                      onValueChange={(value) => setFormData({ ...formData, tax_id: value === NO_SELECTION ? undefined : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('masters.product.selectTax')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_SELECTION}>{t('common.none')}</SelectItem>
                        {taxes.map((tax) => (
                          <SelectItem key={tax.tax_id} value={tax.tax_id}>
                            {tax.tax_name} ({tax.tax_rate}%)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="discount_type">{t('masters.product.discountType')}</Label>
                    <Select
                      value={formData.discount_type || NO_SELECTION}
                      onValueChange={(value) => setFormData({ ...formData, discount_type: value === NO_SELECTION ? undefined : value as DiscountType })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('masters.product.selectDiscountType')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_SELECTION}>{t('common.none')}</SelectItem>
                        <SelectItem value="PERCENTAGE">{t('common.percentage')}</SelectItem>
                        <SelectItem value="FIXED">{t('common.fixed')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="discount_value">{t('masters.product.discountValue')}</Label>
                    <Input
                      id="discount_value"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.discount_value || ''}
                      onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                      disabled={!formData.discount_type}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="fees_id">{t('masters.product.fees')}</Label>
                    <Select
                      value={formData.fees_id || NO_SELECTION}
                      onValueChange={(value) => setFormData({ ...formData, fees_id: value === NO_SELECTION ? undefined : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('masters.product.selectFees')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_SELECTION}>{t('common.none')}</SelectItem>
                        {fees.map((fee) => (
                          <SelectItem key={fee.fees_id} value={fee.fees_id}>
                            {fee.fees_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="description">{t('masters.product.description')}</Label>
                  <Textarea
                    id="description"
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder={t('masters.product.descriptionPlaceholder')}
                    rows={3}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {createMutation.isPending || updateMutation.isPending
                    ? t('common.saving')
                    : editingProduct
                    ? t('masters.product.updateProduct')
                    : t('masters.product.createProduct')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center -mb-1">
        <div className="relative flex-1 min-w-[200px] max-w-[300px]">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10 pointer-events-none bg-white dark:bg-neutral-800 rounded-sm">
            <Search className="h-4 w-4 text-muted-foreground" />
          </div>
          <Input
            placeholder={t('masters.product.searchPlaceholder')}
            value={filters.searchTerm}
            onChange={(e) => {
              setFilters({ ...filters, searchTerm: e.target.value })
              setCurrentPage(1)
            }}
            className="pl-9"
          />
        </div>

        <Select
          value={filters.type}
          onValueChange={(value) => {
            setFilters({ ...filters, type: value })
            setCurrentPage(1)
          }}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder={t('masters.product.allTypes')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_TYPES}>{t('masters.product.allTypes')}</SelectItem>
            <SelectItem value="PRODUCT">{t('masters.product.product')}</SelectItem>
            <SelectItem value="SERVICE">{t('masters.product.service')}</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={filters.status}
          onValueChange={(value) => {
            setFilters({ ...filters, status: value })
            setCurrentPage(1)
          }}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder={t('common.allStatuses')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STATUSES}>{t('common.allStatuses')}</SelectItem>
            <SelectItem value="active">{t('common.active')}</SelectItem>
            <SelectItem value="inactive">{t('common.inactive')}</SelectItem>
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="mr-1 h-4 w-4" />
            {t('common.reset')}
          </Button>
        )}
      </div>

      <ProductTable
        products={paginatedProducts}
        allProducts={products}
        filteredProducts={filteredProducts}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={(id) => deleteMutation.mutate(id)}
        isDeleting={deleteMutation.isPending}
        clearFilters={clearFilters}
        pageSize={pageSize}
        currentPage={currentPage}
        totalRecords={totalRecords}
        totalPages={totalPages}
        startRecord={startRecord}
        endRecord={endRecord}
        onPageSizeChange={handlePageSizeChange}
        onNextPage={goToNextPage}
        onPreviousPage={goToPreviousPage}
        t={t}
        canAddEdit={canAddEdit('PRODUCT_MASTER')}
      />
    </div>
  )
}

function ProductTable({
  products,
  allProducts,
  filteredProducts,
  isLoading,
  onEdit,
  onDelete,
  isDeleting,
  clearFilters,
  pageSize,
  currentPage,
  totalRecords,
  totalPages,
  startRecord,
  endRecord,
  onPageSizeChange,
  onNextPage,
  onPreviousPage,
  t,
  canAddEdit,
}: {
  products: ProductMasterListItem[]
  allProducts: ProductMasterListItem[]
  filteredProducts: ProductMasterListItem[]
  isLoading: boolean
  onEdit: (product: ProductMasterListItem) => void
  onDelete: (id: string) => void
  isDeleting: boolean
  clearFilters: () => void
  pageSize: number
  currentPage: number
  totalRecords: number
  totalPages: number
  startRecord: number
  endRecord: number
  onPageSizeChange: (size: string) => void
  onNextPage: () => void
  onPreviousPage: () => void
  t: (key: string) => string
  canAddEdit: boolean
}) {
  if (isLoading) {
    return <div className="text-center py-8">{t('common.loading')}</div>
  }

  if (allProducts.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          {t('masters.product.noProductsFound')}
        </CardContent>
      </Card>
    )
  }

  if (filteredProducts.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <div className="text-muted-foreground mb-4">
            {t('masters.product.noMatchingProducts')}
          </div>
          <Button variant="outline" onClick={clearFilters}>
            <X className="mr-2 h-4 w-4" />
            {t('common.reset')} {t('common.filters')}
          </Button>
        </CardContent>
      </Card>
    )
  }

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
    }).format(amount)
  }

  return (
    <div className="flex flex-col h-[calc(100vh-280px)]">
      <Card className="flex-1 overflow-hidden flex flex-col">
        <div className="flex-1 overflow-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead>{t('masters.product.productName')}</TableHead>
                <TableHead>{t('masters.product.productType')}</TableHead>
                <TableHead>{t('masters.product.unitType')}</TableHead>
                <TableHead className="text-right">{t('masters.product.price')}</TableHead>
                <TableHead>{t('masters.product.tax')}</TableHead>
                <TableHead>{t('common.status')}</TableHead>
                <TableHead className="w-[120px]">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((product) => (
                <TableRow key={product.product_id}>
                  <TableCell className="font-medium">{product.name}</TableCell>
                  <TableCell>
                    {product.product_type === 'PRODUCT' ? (
                      <Badge variant="secondary">{t('masters.product.product')}</Badge>
                    ) : (
                      <Badge variant="outline">{t('masters.product.service')}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {product.unit_type === 'HOURS' ? t('masters.product.hours') : t('masters.product.quantity')}
                    {product.unit && ` (${product.unit})`}
                  </TableCell>
                  <TableCell className="text-right">{formatCurrency(product.price, product.currency)}</TableCell>
                  <TableCell>{product.tax_name || '-'}</TableCell>
                  <TableCell>
                    {product.is_active ? (
                      <Badge variant="default">{t('common.active')}</Badge>
                    ) : (
                      <Badge variant="secondary">{t('common.inactive')}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {canAddEdit && (
                          <>
                            <DropdownMenuItem onClick={() => onEdit(product)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              {t('common.edit')}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => onDelete(product.product_id)}
                              disabled={isDeleting}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              {t('common.delete')}
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {/* Pagination Controls */}
        <div className="flex items-center gap-3 px-4 py-2 border-t">
          <Select value={String(pageSize)} onValueChange={onPageSizeChange}>
            <SelectTrigger className="w-[70px] h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">
            {startRecord}-{endRecord} {t('common.of')} {totalRecords}
          </span>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onPreviousPage} disabled={currentPage === 1}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onNextPage} disabled={currentPage >= totalPages}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}
